import { t } from "i18next";
import { isUndefined } from "lodash";
import { Trans } from "react-i18next";
import { ReactComponent as LeaderIcon } from "../../icons/leader.svg";
import { ILocationEventsResponseBody } from "../../types/response/ILocationEventsResponseBody";
import { getSiteType } from "../../utils/SiteTypes";
import GridSkeletons from "../skeletons/GridSkeletons";

interface LocationCompletedEventProps {
  eventsCompleted: ILocationEventsResponseBody[];
  locationSiteType: string;
  onEventClick: (locationId: string, eventId: string) => void;
  locationId: string;
}

const LocationCompletedEvent = ({
  eventsCompleted,
  locationSiteType,
  onEventClick,
  locationId
}: LocationCompletedEventProps) => {

  if (isUndefined(eventsCompleted)) {
    return <GridSkeletons gridSize={2} skeletonSize={4} skeletonHeight={'130px'} paddingTop={'16px'} />
  }

  const locationFoundSiteType = getSiteType(locationSiteType)

  return (
    <>
      <div
        className={`grid ${eventsCompleted.length > 2 ? 'grid-rows-2' : 'grid'} gap-x-[20px] gap-y-[12px] w-full mt-[16px] pb-[4px] overflow-x-auto hidden-scroll`}
        style={{
          gridAutoFlow: 'column',
          gridAutoColumns: 'minmax(248px, 1fr)'
        }}
      >
        {eventsCompleted?.map((event) => {
          const eventSiteType = getSiteType(event.category);
          const siteTypeName = eventSiteType?.name || locationFoundSiteType?.name || t('SiteTypes.event');
          const siteTypeIconName = eventSiteType?.iconName || locationFoundSiteType?.iconName
          return (
            <div
              key={event.eventId}
              className="flex cursor-pointer"
              onMouseDown={() => onEventClick(locationId, event.eventId)}
            >
              <div className="relative">
                <img
                  src={event?.baseImageUrl}
                  alt="event_image"
                  className="w-[90px] h-[130px] rounded-[12px] object-cover"
                />
                <span className="absolute bottom-0 content-center text-center items-center justify-center w-full h-[21px] rounded-bl-lg rounded-br-lg bg-black/60 text-pure-white text-[11px] leading-[13px]">
                  {t('LocationCompletedEvent.eventEnd')}
                </span>
              </div>

              <div className="flex flex-col ml-[14px] py-[10px] w-[142px] h-[130px] justify-between">
                <div>
                  <div className="text-main-light-2 dark:text-main-dark-2 text-[16px] leading-[19px] font-semibold text-ellipsis overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                    {event?.name}
                  </div>

                  <div className="flex flex-row items-center text-main-light-3 dark:text-main-dark-3 text-[14px] mt-[6px]">
                    <span className="text-[14px] leading-[16px] text-main-light-3 dark:text-main-dark-3">
                      {
                        <Trans
                          i18nKey='LocationCompletedEvent.eventType'
                          values={{
                            artistOrMembers: event.members.length > 0
                              ? event.members.map((member) => member.name).join(', ')
                              : event.artist.shortName,
                            siteTypeName: siteTypeName
                          }}
                          components={{
                            bold: <span className="font-semibold" />
                          }}
                        />
                      }
                    </span>
                    <img
                      src={`/assets/icons/graphic-3d/${siteTypeIconName || '3D.Place'}.png`}
                      alt="site_type_icon"
                      className="w-[14px] h-[14px] ml-[3px] mb-[1px] inline-block"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex flex-row text-[13px] font-normal gap-[4px] items-center text-main-light-4 dark:text-main-dark-4">
                    <LeaderIcon className="w-[14px] h-[14px] flex-shrink-0 fill-sub-light-1 dark:fill-sub-dark-1" />
                    <div className="leading-[16px] truncate">{event?.organizerTwitterId}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default LocationCompletedEvent;
